/**
 * Uygulama genelinde tek podcast oynatıcısı (2026-10-22, kullanıcı kararı: podcast açıkken uygulamada gezinirken de
 * dinlenebilsin, ekranda yüzen simge + bildirim/kilit ekranı kontrolleri).
 *
 * - Ses öğesi React ağacının dışında (document'e bağlı değil) yaşar: podcast penceresi kapansa da çalmaya devam eder.
 * - Kuyruk: üretim sırasında TTS parçaları sırayla eklenir (append), üretim sürerken parça biterse sonrakini bekler.
 * - Media Session: bildirim alanı / kilit ekranı / kulaklık tuşları (oynat, duraklat, 15 sn geri/ileri, sarma).
 *   Android Chrome / TWA ve web'de doğrudan; iOS uygulamasında arka planda çalması için Info.plist UIBackgroundModes=audio.
 * - Oturumun senaryosu (transcript) da burada tutulur; pencere yeniden açılınca kaldığı yerden gösterilir.
 * - Blob URL'leri oynatıcıya aittir; yeni oturum ya da kapatmada serbest bırakılır.
 */

export interface PodcastSession {
    key: string;               // `${subject}_${unitId}` ya da benzeri
    title: string;
    subtitle?: string;         // ör. "Coğrafya · Uzun"
    subject?: string;
    unit?: { id: string; title: string; description?: string; duration?: string };
    script?: string;
    scriptChunks?: string[];
}

export interface PodcastPlayerState {
    session: PodcastSession | null;
    playing: boolean;
    currentTime: number;
    duration: number;
    index: number;             // çalınan kuyruk parçası
    queueLength: number;
    generating: boolean;       // üretim sürüyor (yeni parça gelecek)
    waiting: boolean;          // parça bitti, sıradaki üretilmeyi bekliyor
    modalOpen: boolean;
    rate: number;
}

type Listener = (s: PodcastPlayerState) => void;

let audio: HTMLAudioElement | null = null;
let queue: string[] = [];
let hasPlayed = false; // bu oturumda bir parça başlatıldı mı (ilk parça henüz gelmediyse false)
const ownedUrls = new Set<string>();
const listeners = new Set<Listener>();
let state: PodcastPlayerState = { session: null, playing: false, currentTime: 0, duration: 0, index: 0, queueLength: 0, generating: false, waiting: false, modalOpen: false, rate: 1 };

const emit = (patch: Partial<PodcastPlayerState>) => {
    state = { ...state, ...patch };
    listeners.forEach(l => l(state));
};

const ensureAudio = (): HTMLAudioElement => {
    if (audio) return audio;
    audio = new Audio();
    audio.preload = 'auto';
    (audio as any).playsInline = true;
    audio.addEventListener('timeupdate', () => { emit({ currentTime: audio!.currentTime }); updatePosition(); });
    audio.addEventListener('loadedmetadata', () => { emit({ duration: Number.isFinite(audio!.duration) ? audio!.duration : 0 }); updatePosition(); });
    audio.addEventListener('play', () => { emit({ playing: true }); setPlaybackState('playing'); });
    audio.addEventListener('pause', () => { emit({ playing: false }); setPlaybackState('paused'); });
    audio.addEventListener('ended', onEnded);
    return audio;
};

function setPlaybackState(s: 'playing' | 'paused' | 'none') {
    try { if ('mediaSession' in navigator) navigator.mediaSession.playbackState = s; } catch { /* desteklenmiyor */ }
}

function updatePosition() {
    try {
        if (!audio || !('mediaSession' in navigator) || !navigator.mediaSession.setPositionState) return;
        const d = audio.duration;
        if (!Number.isFinite(d) || d <= 0) return;
        navigator.mediaSession.setPositionState({ duration: d, position: Math.min(audio.currentTime, d), playbackRate: audio.playbackRate || 1 });
    } catch { /* bazı tarayıcılar konumu desteklemez */ }
}

function setupMediaSession(session: PodcastSession) {
    if (!('mediaSession' in navigator)) return;
    try {
        navigator.mediaSession.metadata = new MediaMetadata({
            title: session.title,
            artist: 'Kapı Hoca Podcast',
            album: session.subtitle || 'KPSS',
            artwork: [
                { src: '/icon-192.png', sizes: '192x192', type: 'image/png' },
                { src: '/icon-512.png', sizes: '512x512', type: 'image/png' },
            ],
        });
        const h = (a: MediaSessionAction, fn: MediaSessionActionHandler | null) => { try { navigator.mediaSession.setActionHandler(a, fn); } catch { /* desteklenmeyen eylem */ } };
        h('play', () => { play(); });
        h('pause', () => pause());
        h('stop', () => close());
        h('seekbackward', d => skip(-(d.seekOffset || 15)));
        h('seekforward', d => skip(d.seekOffset || 15));
        h('seekto', d => { if (typeof d.seekTime === 'number') seek(d.seekTime); });
        h('previoustrack', () => skip(-15));
        h('nexttrack', () => skip(15));
    } catch { /* yok say */ }
}

function onEnded() {
    const next = state.index + 1;
    if (next < queue.length) { playIndex(next); return; }
    if (state.generating) { emit({ waiting: true }); return; } // append() gelince devam eder
    emit({ playing: false, waiting: false });
    // Çalışma planı: sonuna kadar dinlenen alt konu podcast'i konuyu "dinlendi" sayar (services/studyPlanService.ts)
    try {
        const s = state.session;
        if (s?.subject && s.unit?.title) window.dispatchEvent(new CustomEvent('kpss-study-signal', { detail: { subject: s.subject, title: s.unit.title, kind: 'p' } }));
    } catch { /* yok */ }
    setPlaybackState('paused');
}

function playIndex(i: number) {
    const a = ensureAudio();
    hasPlayed = true;
    a.src = queue[i];
    a.playbackRate = state.rate;
    emit({ index: i, currentTime: 0, duration: 0, waiting: false });
    a.play().catch(() => emit({ playing: false }));
}

function revokeAll() {
    ownedUrls.forEach(u => { try { URL.revokeObjectURL(u); } catch { /* yok */ } });
    ownedUrls.clear();
}

/** Yeni oturum: önceki durur ve URL'leri bırakılır. urls boş olabilir (üretim başlarken; append ile gelir). */
export function start(session: PodcastSession, urls: string[], opts: { generating?: boolean; autoplay?: boolean } = {}) {
    const a = ensureAudio();
    a.pause();
    revokeAll();
    queue = [...urls];
    hasPlayed = queue.length > 0;
    urls.forEach(u => u.startsWith('blob:') && ownedUrls.add(u));
    emit({ session, index: 0, queueLength: queue.length, generating: !!opts.generating, waiting: false, currentTime: 0, duration: 0, playing: false });
    setupMediaSession(session);
    if (queue.length) {
        a.src = queue[0];
        a.playbackRate = state.rate;
        if (opts.autoplay !== false) a.play().catch(() => emit({ playing: false }));
    } else if (opts.generating) {
        emit({ waiting: true });
    }
}

/** Üretim sırasında yeni parça; bekleniyorsa hemen çalar */
export function append(url: string) {
    queue.push(url);
    if (url.startsWith('blob:')) ownedUrls.add(url);
    emit({ queueLength: queue.length });
    if (state.waiting) {
        const target = hasPlayed ? state.index + 1 : 0;
        if (target < queue.length) playIndex(target);
    }
}

/** Üretim bitti (son parça geldi). Bekleniyorsa durur. */
export function finishGenerating() {
    emit({ generating: false });
    if (state.waiting && state.index + 1 >= queue.length) emit({ waiting: false, playing: false });
}

/** Oturumun senaryosu/bilgisi güncellenir (ör. üretim sonunda tam metin) */
export function updateSession(patch: Partial<PodcastSession>) {
    if (!state.session) return;
    emit({ session: { ...state.session, ...patch } });
}

export function play() { const a = ensureAudio(); if (a.src) a.play().catch(() => emit({ playing: false })); }
export function pause() { audio?.pause(); }
export function toggle() { if (state.playing) pause(); else play(); }
export function seek(t: number) { if (!audio) return; const d = audio.duration; audio.currentTime = Math.max(0, Number.isFinite(d) ? Math.min(t, d) : t); updatePosition(); }
export function skip(sec: number) {
    if (!audio) return;
    const target = audio.currentTime + sec;
    // Parça sınırını aşan geri sarmada önceki parçaya geç (üretim parçalı çalınırken)
    if (target < 0 && state.index > 0) { playIndex(state.index - 1); return; }
    seek(target);
}
export function setRate(r: number) { emit({ rate: r }); if (audio) audio.playbackRate = r; updatePosition(); }
export function setModalOpen(open: boolean) { emit({ modalOpen: open }); }

/** Tamamen kapat (mini oynatıcıdaki ✕) */
export function close() {
    if (audio) { audio.pause(); audio.removeAttribute('src'); audio.load(); }
    revokeAll();
    queue = [];
    hasPlayed = false;
    emit({ session: null, playing: false, currentTime: 0, duration: 0, index: 0, queueLength: 0, generating: false, waiting: false });
    setPlaybackState('none');
    try { if ('mediaSession' in navigator) navigator.mediaSession.metadata = null; } catch { /* yok */ }
}

export function getState() { return state; }
export function subscribe(l: Listener): () => void { listeners.add(l); return () => { listeners.delete(l); }; }
